/**
 * =============================================================================
 * Página: /afiliado/saques
 * Versão: 2.0.0 (Redesign Premium Mobile-First)
 *
 * Descrição:
 *   Solicitação de saque + histórico do afiliado, com visual premium e CTA
 *   destacado em gradiente.
 * =============================================================================
 */
import { useState } from 'react';
import { Banknote, Plus, Paperclip, AlertCircle } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { PayoutAttachmentsManager } from '@/components/referral/PayoutAttachmentsManager';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useAffiliatePayouts,
  type AffiliatePayoutRow,
  type PayoutAttachmentLite,
  type PayoutStatus,
  type PayoutMethod,
} from '@/hooks/useAffiliatePayouts';
import { RequestPayoutModal } from '@/components/affiliate/RequestPayoutModal';
import { AffiliateProgramsBreakdownCard } from '@/components/affiliate/AffiliateProgramsBreakdownCard';
import { formatCurrency } from '@/lib/currency-utils';
import { AffiliatePageHeader } from '@/components/affiliate/shared/AffiliatePageHeader';
import { AffiliateEmptyState } from '@/components/affiliate/shared/AffiliateEmptyState';
import {
  AttachmentThumbnail,
  type BaseAttachment,
} from '@/components/shared/AttachmentThumbnail';
import {
  mimeToFileType,
  getPayoutAttachmentPublicUrl,
} from '@/hooks/useReferralPayoutAttachments';
import { cn } from '@/lib/utils';

/**
 * Converte um anexo "lite" (vindo embutido no payout) para o shape genérico
 * aceito pelo componente compartilhado `AttachmentThumbnail`.
 */
function payoutAttachmentToBase(att: PayoutAttachmentLite): BaseAttachment {
  const publicUrl = getPayoutAttachmentPublicUrl(att.storage_path);
  const fileType = mimeToFileType(att.mime_type);
  return {
    id: att.id,
    file_name: att.file_name,
    file_type: fileType,
    public_url: publicUrl,
    thumbnail_url: fileType === 'image' ? publicUrl : null,
    file_size: att.file_size,
  };
}

// Status renderizado como "dot colorido + texto preto" (sem fundo/badge),
// padronizando com Indicações e Comissões — visual editorial limpo.
const STATUS_META: Record<PayoutStatus, { label: string; dotClass: string }> = {
  pending:    { label: 'Pendente',    dotClass: 'bg-[hsl(var(--aff-muted-foreground))]' },
  processing: { label: 'Processando', dotClass: 'bg-[hsl(var(--aff-primary))]' },
  paid:       { label: 'Pago',        dotClass: 'bg-[hsl(var(--aff-success))]' },
  failed:     { label: 'Falhou',      dotClass: 'bg-[hsl(var(--aff-danger))]' },
  cancelled:  { label: 'Cancelado',   dotClass: 'bg-[hsl(var(--aff-danger))]' },
};

const METHOD_LABEL: Record<PayoutMethod, string> = {
  pix: 'PIX',
  bank_transfer: 'Transferência',
  credits: 'Créditos',
  manual: 'Manual',
};

function statusBadge(status: PayoutStatus, failedReason?: string | null) {
  const meta = STATUS_META[status] ?? { label: status, dotClass: 'bg-[hsl(var(--aff-muted-foreground))]' };
  // Mostra ícone de motivo apenas para status "failed" com texto preenchido
  const showReason = status === 'failed' && !!failedReason;
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[hsl(var(--aff-foreground))]">
      <span aria-hidden className={cn('h-2 w-2 rounded-full shrink-0', meta.dotClass)} />
      {meta.label}
      {showReason && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Ver motivo da falha"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center justify-center text-[hsl(var(--aff-danger))] hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--aff-danger))] rounded-full"
            >
              <AlertCircle className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="w-72 text-xs">
            <p className="font-semibold mb-1 text-[hsl(var(--aff-foreground))]">
              Motivo da falha
            </p>
            <p className="text-[hsl(var(--aff-muted-foreground))] whitespace-pre-wrap break-words">
              {failedReason}
            </p>
          </PopoverContent>
        </Popover>
      )}
    </span>
  );
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export default function AffiliatePayouts() {
  const { data: payouts, isLoading, isError, refetch } = useAffiliatePayouts();
  const [modalOpen, setModalOpen] = useState(false);
  // Estado: pagamento selecionado para visualizar comprovantes
  const [proofPayoutId, setProofPayoutId] = useState<string | null>(null);

  return (
    <div className="container mx-auto max-w-6xl p-4 md:p-6 space-y-6 aff-animate-in">
      <AffiliatePageHeader
        icon={<Banknote className="h-5 w-5" />}
        title="Saques"
        subtitle="Solicite saques e acompanhe o histórico de pagamentos."
        actions={
          <Button
            size="sm"
            onClick={() => setModalOpen(true)}
            className="text-[hsl(var(--aff-primary-foreground))] hover:opacity-90 shadow-[var(--aff-shadow-soft)] border-0"
            style={{ background: 'var(--aff-gradient-hero)' }}
          >
            <Plus className="h-4 w-4" />
            Solicitar saque
          </Button>
        }
      />

      {/* Saldo e mínimo de saque por programa */}
      <AffiliateProgramsBreakdownCard />

      {isLoading && <PayoutsSkeleton />}

      {isError && !isLoading && (
        <div className="aff-card flex flex-col items-center gap-3 px-6 py-10 text-center">
          <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
            Não foi possível carregar seu histórico de saques.
          </p>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Tentar novamente
          </Button>
        </div>
      )}

      {!isLoading && !isError && (payouts?.length ?? 0) === 0 && (
        <AffiliateEmptyState
          icon={<Banknote className="h-7 w-7" />}
          title="Nenhum saque solicitado"
          description="Quando você solicitar seu primeiro saque, ele aparecerá aqui no histórico."
          action={
            <Button
              size="sm"
              onClick={() => setModalOpen(true)}
              className="text-[hsl(var(--aff-primary-foreground))] hover:opacity-90 shadow-[var(--aff-shadow-soft)] border-0"
              style={{ background: 'var(--aff-gradient-hero)' }}
            >
              <Plus className="h-4 w-4" />
              Solicitar saque
            </Button>
          }
        />
      )}

      {!isLoading && !isError && (payouts?.length ?? 0) > 0 && (
        <>
          {/* Desktop */}
          <div className="aff-card hidden md:block overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-[hsl(var(--aff-border))] hover:bg-transparent">
                  <TableHead>Solicitado em</TableHead>
                  <TableHead>Método</TableHead>
                  <TableHead>Comissões</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Pago em</TableHead>
                  <TableHead className="text-center">Comprov.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payouts!.map((p) => (
                  <TableRow
                    key={p.id}
                    className="border-[hsl(var(--aff-border))] hover:bg-[hsl(var(--aff-surface-2))]"
                  >
                    <TableCell className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                      {formatDate(p.requested_at)}
                    </TableCell>
                    <TableCell className="text-sm text-[hsl(var(--aff-foreground))]">
                      {METHOD_LABEL[p.payout_method] ?? p.payout_method}
                    </TableCell>
                    <TableCell className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                      {p.commissions_count}
                    </TableCell>
                    <TableCell>{statusBadge(p.status, p.failed_reason)}</TableCell>
                    <TableCell className="text-right font-bold tabular-nums text-[hsl(var(--aff-foreground))]">
                      {formatCurrency(Number(p.amount))}
                    </TableCell>
                    <TableCell className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                      {formatDate(p.paid_at)}
                    </TableCell>
                    <TableCell className="text-center">
                      <PayoutAttachmentsInline
                        attachments={p.attachments}
                        maxVisible={3}
                        onOpenAll={() => setProofPayoutId(p.id)}
                        size="sm"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile */}
          <div className="grid gap-3 md:hidden">
            {payouts!.map((p) => (
              <MobilePayoutRow
                key={p.id}
                payout={p}
                onOpenProofs={() => setProofPayoutId(p.id)}
              />
            ))}
          </div>
        </>
      )}

      <RequestPayoutModal open={modalOpen} onOpenChange={setModalOpen} />

      {/* Modal: comprovantes de pagamento (somente leitura para o afiliado) */}
      <Dialog
        open={!!proofPayoutId}
        onOpenChange={(open) => !open && setProofPayoutId(null)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Comprovantes de pagamento</DialogTitle>
            <DialogDescription>
              Arquivos anexados pelo administrador como comprovante deste saque.
            </DialogDescription>
          </DialogHeader>
          <PayoutAttachmentsManager
            payoutId={proofPayoutId}
            readOnly
            title="Arquivos disponíveis"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MobilePayoutRow({
  payout: p,
  onOpenProofs,
}: {
  payout: AffiliatePayoutRow;
  onOpenProofs: () => void;
}) {
  return (
    <div className="aff-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-lg font-bold tabular-nums text-[hsl(var(--aff-foreground))]">
            {formatCurrency(Number(p.amount))}
          </div>
          <div className="text-[11px] text-[hsl(var(--aff-muted-foreground))]">
            {METHOD_LABEL[p.payout_method] ?? p.payout_method} · {p.commissions_count}{' '}
            comissão(ões)
          </div>
        </div>
        {statusBadge(p.status, p.failed_reason)}
      </div>

      <div className="flex items-center justify-between border-t border-[hsl(var(--aff-border))] pt-3 text-[11px] text-[hsl(var(--aff-muted-foreground))]">
        <span>Solicitado: {formatDate(p.requested_at)}</span>
        {p.paid_at && <span>Pago: {formatDate(p.paid_at)}</span>}
      </div>

      {/*
        Comprovantes inline: substitui o antigo botão "Ver comprovantes".
        Se não houver anexos, a seção é totalmente omitida (sem placeholder).
      */}
      {p.attachments.length > 0 && (
        <div className="border-t border-[hsl(var(--aff-border))] pt-3 space-y-2">
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-[hsl(var(--aff-muted-foreground))]">
            <Paperclip className="h-3 w-3" />
            <span>Comprovantes ({p.attachments.length})</span>
          </div>
          <PayoutAttachmentsInline
            attachments={p.attachments}
            maxVisible={6}
            onOpenAll={onOpenProofs}
            size="md"
          />
        </div>
      )}
    </div>
  );
}

/**
 * Galeria compacta de miniaturas de comprovantes para um payout.
 *
 * - Reaproveita `AttachmentThumbnail` (mesmo componente usado em
 *   tarefas/CRM/comentários), garantindo consistência visual e ações
 *   (download via overlay no hover, tooltip com nome/tamanho).
 * - Limita a quantidade visível em `maxVisible`; o restante vira um
 *   contador `+N` que abre o modal completo de comprovantes.
 * - Clicar em uma miniatura abre o arquivo em nova aba.
 */
function PayoutAttachmentsInline({
  attachments,
  maxVisible,
  onOpenAll,
  size = 'sm',
}: {
  attachments: PayoutAttachmentLite[];
  maxVisible: number;
  onOpenAll: () => void;
  size?: 'sm' | 'md';
}) {
  if (!attachments || attachments.length === 0) {
    return (
      <span className="text-[11px] text-[hsl(var(--aff-muted-foreground))]">—</span>
    );
  }

  const visible = attachments.slice(0, maxVisible);
  const overflow = attachments.length - visible.length;
  // Tamanhos: sm para tabela desktop, md para cards mobile
  const tileClass = size === 'sm' ? 'w-12 h-12' : 'w-14 h-14';

  return (
    <div className="flex flex-wrap items-center gap-1.5 justify-start">
      {visible.map((att) => {
        const base = payoutAttachmentToBase(att);
        return (
          <AttachmentThumbnail
            key={att.id}
            attachment={base}
            readOnly
            className={tileClass}
            onClick={() =>
              window.open(base.public_url, '_blank', 'noopener,noreferrer')
            }
          />
        );
      })}
      {overflow > 0 && (
        <button
          type="button"
          onClick={onOpenAll}
          className={cn(
            tileClass,
            'rounded-lg border border-[hsl(var(--aff-border))] bg-[hsl(var(--aff-surface-2))]',
            'flex items-center justify-center text-[11px] font-semibold',
            'text-[hsl(var(--aff-foreground))] hover:bg-[hsl(var(--aff-surface))] transition-colors',
          )}
          title="Ver todos os comprovantes"
        >
          +{overflow}
        </button>
      )}
    </div>
  );
}

function PayoutsSkeleton() {
  return (
    <div className="aff-card p-4 space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-md" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-6 w-20" />
        </div>
      ))}
    </div>
  );
}
